/**
 * Provider-agnostic buoy station registry.
 *
 * Today we have two providers feeding the same surface area:
 *   - `ndbc`  → NOAA NDBC realtime2 (fetched live from
 *     `https://www.ndbc.noaa.gov/data/realtime2/<id>.txt`)
 *   - `cmems` → Copernicus Marine in-situ, pulled hourly by a GitHub
 *     Action and cached in Firestore (`observationsCache/<station-id>`)
 *     because CMEMS's auth model is not serverless-friendly.
 *
 * Station IDs returned from this module are *namespaced* — e.g.
 * `ndbc:46050` and `cmems:Cabo_Silleiro`. Callers must treat them as
 * opaque strings; provider dispatch happens server-side in
 * `api/buoys.ts` and `api/cron/backfill-observations.ts` by
 * `parseStationId(id).provider`.
 *
 * Back-compat: pre-namespace forecast snapshots wrote bare numeric IDs
 * (e.g. `"44017"`). The dispatch helpers treat any unprefixed ID as
 * NDBC.
 */

import {
  classifyBasin,
  NDBC_STATIONS,
  type NDBCStation,
  type OceanBasin,
} from "./ndbc-stations.js";
import { CMEMS_STATIONS, type CMEMSStation } from "./cmems-stations.js";

export type BuoyProvider = "ndbc" | "cmems";

export interface BuoyStation {
  /** Namespaced ID: `<provider>:<raw-id>`. */
  id: string;
  provider: BuoyProvider;
  name: string;
  lat: number;
  lon: number;
  type: "nearshore" | "offshore";
}

/* ---------- Provider regions ---------- */

/**
 * Regional bbox used to keep CMEMS Iberian buoys from being matched to
 * locations on the wrong continent (and vice versa). NDBC has its own
 * `classifyBasin` for the same reason.
 */
const IBERIA_BBOX = {
  latMin: 34,
  latMax: 46,
  lonMin: -12,
  lonMax: 4,
};

function inIberia(lat: number, lon: number): boolean {
  return (
    lat >= IBERIA_BBOX.latMin &&
    lat <= IBERIA_BBOX.latMax &&
    lon >= IBERIA_BBOX.lonMin &&
    lon <= IBERIA_BBOX.lonMax
  );
}

/* ---------- Catalogue ---------- */

function fromNDBC(s: NDBCStation): BuoyStation {
  return {
    id: `ndbc:${s.id}`,
    provider: "ndbc",
    name: s.name,
    lat: s.lat,
    lon: s.lon,
    type: s.type,
  };
}

function fromCMEMS(s: CMEMSStation): BuoyStation {
  return {
    id: s.id,
    provider: "cmems",
    name: s.name,
    lat: s.lat,
    lon: s.lon,
    type: s.type,
  };
}

export const BUOY_STATIONS: BuoyStation[] = [
  ...NDBC_STATIONS.map(fromNDBC),
  ...CMEMS_STATIONS.map(fromCMEMS),
];

/* ---------- ID parsing ---------- */

export interface ParsedStationId {
  provider: BuoyProvider;
  /** The provider-internal station code with the prefix stripped. */
  rawId: string;
}

/**
 * Parse a namespaced station ID. Unprefixed IDs (legacy `"44017"`-style
 * snapshots) are treated as NDBC so the back-fill cron keeps working on
 * existing Firestore rows.
 */
export function parseStationId(id: string): ParsedStationId | null {
  if (!id) return null;
  const colon = id.indexOf(":");
  if (colon < 0) {
    // Legacy bare ID. NDBC IDs are always alphanumeric; reject anything
    // else so we don't silently route garbage through the NDBC path.
    if (/^[A-Za-z0-9]{3,8}$/.test(id)) {
      return { provider: "ndbc", rawId: id };
    }
    return null;
  }
  const prefix = id.slice(0, colon);
  const rest = id.slice(colon + 1);
  if (!rest) return null;
  if (prefix === "ndbc") return { provider: "ndbc", rawId: rest };
  if (prefix === "cmems") return { provider: "cmems", rawId: rest };
  return null;
}

/* ---------- Lookup ---------- */

function haversineKm(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const R = 6371;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lon2 - lon1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLon / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

/**
 * Return true when `station` is eligible to serve observations for the
 * input lat/lon. Each provider has its own "stay inside the right
 * ocean" filter:
 *   - NDBC: existing basin classifier (Gulf vs Atlantic vs Pacific…).
 *   - CMEMS: only matches inside the Iberian bbox.
 * Locations outside any provider's home region fall through to raw
 * nearest-station within `maxDistKm`.
 */
function isCompatible(
  station: BuoyStation,
  userLat: number,
  userLon: number,
  userBasin: OceanBasin,
): boolean {
  if (station.provider === "cmems") {
    return inIberia(userLat, userLon);
  }
  // NDBC: only enforce basin equality when the user's basin is known.
  if (userBasin === "other") return true;
  return classifyBasin(station.lat, station.lon) === userBasin;
}

/**
 * Find the nearest buoy to `(lat, lon)` across every provider.
 * Returns `null` if no station sits within `maxDistKm`.
 */
export function findNearestBuoy(
  lat: number,
  lon: number,
  maxDistKm = 500,
): { station: BuoyStation; distanceKm: number } | null {
  const userBasin = classifyBasin(lat, lon);
  let best: BuoyStation | null = null;
  let bestDist = Infinity;
  for (const s of BUOY_STATIONS) {
    if (!isCompatible(s, lat, lon, userBasin)) continue;
    const d = haversineKm(lat, lon, s.lat, s.lon);
    if (d < bestDist) {
      bestDist = d;
      best = s;
    }
  }
  if (!best || bestDist > maxDistKm) return null;
  return { station: best, distanceKm: Math.round(bestDist * 10) / 10 };
}

/**
 * Find the nearest nearshore + offshore buoys across every provider.
 * Same provider-region filter as `findNearestBuoy`.
 */
export function findNearestBuoys(
  lat: number,
  lon: number,
  maxDistKm = 500,
): { nearshore: BuoyStation | null; offshore: BuoyStation | null } {
  const userBasin = classifyBasin(lat, lon);
  let bestNearshore: BuoyStation | null = null;
  let bestNearshoreDist = Infinity;
  let bestOffshore: BuoyStation | null = null;
  let bestOffshoreDist = Infinity;

  for (const s of BUOY_STATIONS) {
    if (!isCompatible(s, lat, lon, userBasin)) continue;
    const d = haversineKm(lat, lon, s.lat, s.lon);
    if (d > maxDistKm) continue;

    if (s.type === "nearshore" && d < bestNearshoreDist) {
      bestNearshoreDist = d;
      bestNearshore = s;
    } else if (s.type === "offshore" && d < bestOffshoreDist) {
      bestOffshoreDist = d;
      bestOffshore = s;
    }
  }

  return { nearshore: bestNearshore, offshore: bestOffshore };
}
